import { cn } from '@/utils/cn'
import { focusRing } from '@/components/ui/buttonStyles'

interface SrsSliderProps {
  id: string
  label: string
  hint: string
  value: number
  min: number
  max: number
  step: number
  suffix?: string
  /** Replaces value + suffix in the readout, e.g. to show a fraction as a percentage. */
  format?: (value: number) => string
  onChange: (value: number) => void
  /** The persisted value: marked on the track, with a reset back to it once moved. */
  savedValue?: number
  /** Keeps the hint for assistive tech only, where the slider sits in a tight row. */
  compact?: boolean
}

/** The native thumb is ~16px wide, so its centre travels 8px in from each end. */
const THUMB = 16

export function SrsSlider({
  id,
  label,
  hint,
  value,
  min,
  max,
  step,
  suffix,
  format,
  onChange,
  savedValue,
  compact = false,
}: SrsSliderProps) {
  const show = (v: number) => (format ? format(v) : `${v}${suffix ?? ''}`)
  const moved = savedValue !== undefined && savedValue !== value
  const savedPct = savedValue === undefined ? 0 : ((savedValue - min) / (max - min)) * 100

  return (
    <div className={compact ? 'space-y-0.5' : 'space-y-1.5'}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="font-mono text-xs uppercase tracking-widest text-muted">
          {label}
        </label>
        <span className="flex items-baseline gap-2">
          {moved && (
            <button
              type="button"
              onClick={() => onChange(savedValue)}
              aria-label={`Reset ${label} to saved ${show(savedValue)}`}
              className={cn('rounded px-1 font-mono text-xs text-muted transition-colors duration-200 hover:text-body', focusRing)}
            >
              was {show(savedValue)} ↺
            </button>
          )}
          <output htmlFor={id} className={cn('font-mono text-sm', moved ? 'text-em' : 'text-accent')}>
            {show(value)}
          </output>
        </span>
      </div>
      <div className="relative">
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          aria-describedby={`${id}-hint`}
          onChange={(event) => onChange(Number(event.target.value))}
          className="h-11 w-full cursor-pointer accent-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
        />
        {moved && (
          <span
            aria-hidden
            className="pointer-events-none absolute top-1/2 h-4 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted"
            style={{ left: `calc(${savedPct}% + ${(0.5 - savedPct / 100) * THUMB}px)` }}
          />
        )}
      </div>
      <p id={`${id}-hint`} className={compact ? 'sr-only' : 'font-serif text-sm text-muted'}>
        {hint}
      </p>
    </div>
  )
}
